/**
 * Revision R3 — Rich-Text-Modus des MD-Editors (Nutzerwunsch: „schreiben,
 * aber Tabellen etc. formatiert sehen statt Raw-Markdown").
 *
 * Zwei Bausteine:
 *   1. `domToMarkdown` — die Rückrichtung zu `renderMarkdown` (markdown.ts):
 *      der WYSIWYG-Modus editiert das gerenderte HTML (contentEditable) und
 *      serialisiert es hier zurück in Markdown. Markdown bleibt die EINE
 *      Quelle der Wahrheit (Speicherformat, MCP, Snapshot unverändert).
 *      Unterstützt exakt die Formen, die der Renderer erzeugt, plus die
 *      Varianten, die contentEditable/execCommand produziert (b/i/div).
 *   2. Dokumenteinstellungen (Seitenformat/Ränder) als YAML-Frontmatter im
 *      Markdown selbst — sichtbar/portabel statt Datenbank-Sonderfeld; wirkt
 *      auf den PDF-Export und die Editor-Darstellung.
 */

export interface DocSettings {
  seite: "a4" | "a4quer" | "letter";
  raender: "schmal" | "normal" | "breit";
  // R4 (Punkt 11) — Textausrichtung der Ansicht (Blocksatz ist in Markdown
  // nicht abbildbar und lebt daher hier, bei den Dokumenteinstellungen).
  ausrichtung?: "links" | "blocksatz";
}

export const DEFAULT_DOC_SETTINGS: DocSettings = {
  seite: "a4",
  raender: "normal",
};

const PAGE_SIZES: Record<DocSettings["seite"], { w: number; h: number }> = {
  a4: { w: 595, h: 842 },
  a4quer: { w: 842, h: 595 },
  letter: { w: 612, h: 792 },
};

const MARGIN_PT: Record<DocSettings["raender"], number> = {
  schmal: 28,
  normal: 48,
  breit: 76,
};

/** PDF-Writer-Optionen (file-export.textToPdf) aus den Dokumenteinstellungen. */
export const pdfOptionsFor = (
  settings: DocSettings | null,
): { pageW: number; pageH: number; margin: number } => {
  const s = settings ?? DEFAULT_DOC_SETTINGS;
  const page = PAGE_SIZES[s.seite] ?? PAGE_SIZES.a4;
  return {
    pageW: page.w,
    pageH: page.h,
    margin: MARGIN_PT[s.raender] ?? MARGIN_PT.normal,
  };
};

const SETTING_KEYS = new Set(["seite", "raender", "ausrichtung"]);

/**
 * Frontmatter lesen: NUR ein führender `---`-Block, dessen Zeilen sämtlich
 * bekannte `key: value`-Paare sind, gilt als Einstellungs-Frontmatter — ein
 * Dokument, das zufällig mit einer `---`-Trennlinie beginnt, bleibt unberührt.
 */
export const parseDocSettings = (
  md: string,
): { settings: DocSettings | null; body: string } => {
  const m = /^---\n([\s\S]*?)\n---\n?/.exec(md ?? "");
  if (!m) {
    return { settings: null, body: md ?? "" };
  }
  const lines = m[1].split("\n").filter((l) => l.trim() !== "");
  const pairs: Record<string, string> = {};
  for (const line of lines) {
    const kv = /^([a-z]+):\s*(\S+)\s*$/.exec(line.trim());
    if (!kv || !SETTING_KEYS.has(kv[1])) {
      return { settings: null, body: md };
    }
    pairs[kv[1]] = kv[2];
  }
  if (Object.keys(pairs).length === 0) {
    return { settings: null, body: md };
  }
  const settings: DocSettings = {
    ...(pairs.ausrichtung === "blocksatz"
      ? { ausrichtung: "blocksatz" as const }
      : {}),
    seite: (["a4", "a4quer", "letter"] as const).includes(
      pairs.seite as DocSettings["seite"],
    )
      ? (pairs.seite as DocSettings["seite"])
      : DEFAULT_DOC_SETTINGS.seite,
    raender: (["schmal", "normal", "breit"] as const).includes(
      pairs.raender as DocSettings["raender"],
    )
      ? (pairs.raender as DocSettings["raender"])
      : DEFAULT_DOC_SETTINGS.raender,
  };
  return { settings, body: md.slice(m[0].length) };
};

/** Einstellungen ins Dokument schreiben (Frontmatter ersetzen bzw. voranstellen). */
export const withDocSettings = (md: string, settings: DocSettings): string => {
  const { body } = parseDocSettings(md ?? "");
  const align =
    settings.ausrichtung === "blocksatz" ? "ausrichtung: blocksatz\n" : "";
  const fm = `---\nseite: ${settings.seite}\nraender: ${settings.raender}\n${align}---\n`;
  return fm + body;
};

/* ---- DOM → Markdown ------------------------------------------------------ */

const inlineOf = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }
  const el = node as HTMLElement;
  const inner = Array.from(el.childNodes).map(inlineOf).join("");
  switch (el.tagName) {
    case "STRONG":
    case "B":
      return inner.trim() === "" ? inner : `**${inner}**`;
    case "EM":
    case "I":
      return inner.trim() === "" ? inner : `*${inner}*`;
    case "CODE":
      return `\`${inner}\``;
    case "A": {
      const href = el.getAttribute("href") || "";
      if (!href) {
        return inner;
      }
      // Welle B (Punkt 15) — Button trägt den Anzeige-Typ im Titel-Slot zurück.
      if (el.getAttribute("data-mdtype") === "button") {
        return `[${inner}](${href} "button")`;
      }
      return `[${inner}](${href})`;
    }
    // R4 (Bilder-Befund) — <img> wird zu ![alt](src); vorher wurden Bilder
    // beim Zurückschreiben stumm verworfen (eingefügte Screenshots
    // verschwanden mit dem nächsten Speichern).
    case "IMG": {
      const src = el.getAttribute("src") || "";
      const alt = el.getAttribute("alt") || "Bild";
      return src ? `![${alt}](${src})` : "";
    }
    // Checkbox-Inputs werden auf Block-Ebene (LI) behandelt.
    case "INPUT":
      return "";
    case "BR":
      return "\n";
    case "SPAN": {
      // Welle B (Punkt 15) — der Vorschau-„Atom"-Chip serialisiert zurück in
      // `[Label](ziel "preview:modus")`. Attribute sind die Quelle; der
      // sichtbare Chip-Text (📄 … ↗) wird bewusst nicht als Label genommen.
      if ((el as HTMLElement).classList?.contains("ryfe-mdpreview")) {
        const href = el.getAttribute("data-mdhref") || "";
        const mode = el.getAttribute("data-mdmode") || "fit";
        const label = el.getAttribute("data-mdlabel") || "Vorschau";
        return href ? `[${label}](${href} "preview:${mode}")` : label;
      }
      return inner;
    }
    default:
      // FONT/… aus Paste/execCommand: nur der Text zählt.
      return inner;
  }
};

const alignSpec = (cell: HTMLElement): string => {
  const a = cell.style?.textAlign;
  if (a === "center") {
    return ":---:";
  }
  if (a === "right") {
    return "---:";
  }
  if (a === "left") {
    return ":---";
  }
  return "---";
};

const tableToMarkdown = (table: HTMLElement): string => {
  const headRow = table.querySelector("thead tr");
  const headCells = headRow
    ? (Array.from(headRow.children) as HTMLElement[])
    : [];
  const bodyRows = Array.from(
    table.querySelectorAll("tbody tr"),
  ) as HTMLElement[];
  // Ohne thead (Paste): erste Zeile wird Kopf.
  const allRows = Array.from(table.querySelectorAll("tr")) as HTMLElement[];
  const head = headCells.length
    ? headCells
    : (Array.from(allRows[0]?.children ?? []) as HTMLElement[]) || [];
  const body = headCells.length ? bodyRows : allRows.slice(1);
  if (head.length === 0) {
    return "";
  }
  const line = (cells: HTMLElement[]) =>
    `| ${cells
      .map((c) => Array.from(c.childNodes).map(inlineOf).join("").trim())
      .join(" | ")} |`;
  const out = [line(head), `| ${head.map(alignSpec).join(" | ")} |`];
  for (const row of body) {
    out.push(line(Array.from(row.children) as HTMLElement[]));
  }
  return out.join("\n");
};

interface Block {
  md: string;
  type: string;
}

/**
 * R4 (Punkt 11) — eine (ggf. verschachtelte) Liste in Markdown-Zeilen.
 * Verschachtelte ULs/OLs können als Kind des LI ODER als Geschwister im
 * Listen-Element stehen (der Renderer erzeugt Letzteres, contentEditable-
 * Indent Ersteres) — beide Formen werden gelesen.
 */
function listToLines(list: HTMLElement, depth: number): string[] {
  const ordered = list.tagName === "OL";
  const pad = "  ".repeat(depth);
  const out: string[] = [];
  let idx = 0;
  for (const child of Array.from(list.children) as HTMLElement[]) {
    if (child.tagName === "UL" || child.tagName === "OL") {
      out.push(...listToLines(child, depth + 1));
      continue;
    }
    if (child.tagName !== "LI") {
      continue;
    }
    // Checkbox als erstes Element → Checklisten-Item.
    const box = Array.from(child.children).find(
      (c) =>
        c.tagName === "INPUT" && (c as HTMLInputElement).type === "checkbox",
    ) as HTMLInputElement | undefined;
    const inlineNodes = Array.from(child.childNodes).filter(
      (n) =>
        !(
          n.nodeType === Node.ELEMENT_NODE &&
          ["UL", "OL", "INPUT"].includes((n as HTMLElement).tagName)
        ),
    );
    const text = inlineNodes.map(inlineOf).join("").trim();
    idx += 1;
    const marker = ordered ? `${idx}.` : "-";
    const check = box ? (box.checked ? "[x] " : "[ ] ") : "";
    out.push(`${pad}${marker} ${check}${text}`);
    // Verschachtelte Liste INNERHALB des LI (contentEditable-Indent).
    for (const nested of Array.from(child.children) as HTMLElement[]) {
      if (nested.tagName === "UL" || nested.tagName === "OL") {
        out.push(...listToLines(nested, depth + 1));
      }
    }
  }
  return out;
}

const blockOf = (el: HTMLElement): Block | null => {
  const tag = el.tagName;
  const heading = /^H([1-6])$/.exec(tag);
  if (heading) {
    const text = Array.from(el.childNodes).map(inlineOf).join("").trim();
    return { md: `${"#".repeat(Number(heading[1]))} ${text}`, type: "h" };
  }
  switch (tag) {
    case "P":
    case "DIV": {
      const text = Array.from(el.childNodes).map(inlineOf).join("").trim();
      return text === "" ? null : { md: text, type: "p" };
    }
    case "HR":
      return { md: "---", type: "hr" };
    case "BLOCKQUOTE": {
      const text = Array.from(el.childNodes).map(inlineOf).join("").trim();
      return { md: `> ${text}`, type: "quote" };
    }
    case "UL":
    case "OL": {
      // R4 (Punkt 11) — verschachtelte Listen (2 Leerzeichen je Ebene) und
      // Checklisten-Items (führende Checkbox → `- [ ]` / `- [x]`).
      const lines = listToLines(el, 0);
      return lines.length ? { md: lines.join("\n"), type: "list" } : null;
    }
    case "PRE": {
      // renderMarkdown fügt um den Code je ein Zeilenumbruch-Paar ein
      // (<pre><code>\n…\n</code></pre>) — genau eines je Seite abstreifen.
      const code = (el.textContent ?? "").replace(/^\n/, "").replace(/\n$/, "");
      return { md: `\`\`\`\n${code}\n\`\`\``, type: "code" };
    }
    case "TABLE": {
      const md = tableToMarkdown(el);
      return md ? { md, type: "table" } : null;
    }
    default: {
      // Unbekannter Block (Paste): als Absatz-Text übernehmen.
      const text = Array.from(el.childNodes).map(inlineOf).join("").trim();
      return text === "" ? null : { md: text, type: "p" };
    }
  }
};

/**
 * Die Rückrichtung zu `renderMarkdown`: das (editierte) HTML des WYSIWYG-
 * Modus zurück in Markdown. Aufeinanderfolgende Blockquote-Zeilen bleiben
 * zusammenhängend; alle anderen Blöcke trennt eine Leerzeile.
 */
// Block-level tags. A wrapper <div>/<p> that contains ANY of these is NOT an
// inline paragraph — contentEditable loves to wrap a list/table inside a <div>,
// and flattening that through inlineOf collapsed whole lists onto one line
// (Kommentar P3: „alle Bulletpoints in einer Zeile"). We descend into such
// wrappers instead so each block (incl. UL/OL) is serialized on its own.
const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "UL",
  "OL",
  "TABLE",
  "PRE",
  "BLOCKQUOTE",
  "HR",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
]);
const hasBlockChild = (el: HTMLElement): boolean =>
  Array.from(el.children).some((c) => BLOCK_TAGS.has(c.tagName));

const collectBlocks = (root: HTMLElement, blocks: Block[]): void => {
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? "").trim();
      if (text !== "") {
        blocks.push({ md: text, type: "p" });
      }
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }
    const el = node as HTMLElement;
    // A wrapper (DIV/P — or a BLOCKQUOTE produced by execCommand("indent"))
    // holding block-level children → descend, don't flatten it into one inline
    // paragraph. Flattening dropped bullets (P3) and, for a Tab-indented table,
    // collapsed the whole table into a `> cell1cell2…` quote line (P15). A real
    // blockquote from the renderer holds only inline text (no block child), so
    // it still serializes as a quote.
    if (
      (el.tagName === "DIV" ||
        el.tagName === "P" ||
        el.tagName === "BLOCKQUOTE") &&
      hasBlockChild(el)
    ) {
      collectBlocks(el, blocks);
      continue;
    }
    const block = blockOf(el);
    if (block) {
      blocks.push(block);
    }
  }
};

/**
 * P15 — Tab inside a WYSIWYG table cell must move to the next/previous cell, not
 * indent (execCommand("indent") wraps the table in a blockquote and destroys
 * it). Returns the adjacent `<td>/<th>` in document order, or null at the edges.
 */
export const adjacentTableCell = (
  cell: HTMLElement,
  previous: boolean,
): HTMLElement | null => {
  const table = cell.closest("table");
  if (!table) {
    return null;
  }
  const cells = Array.from(table.querySelectorAll("th,td")) as HTMLElement[];
  const idx = cells.indexOf(cell);
  if (idx < 0) {
    return null;
  }
  return cells[previous ? idx - 1 : idx + 1] ?? null;
};

export const domToMarkdown = (root: HTMLElement): string => {
  const blocks: Block[] = [];
  collectBlocks(root, blocks);
  let out = "";
  blocks.forEach((b, i) => {
    if (i > 0) {
      const prev = blocks[i - 1];
      out += prev.type === "quote" && b.type === "quote" ? "\n" : "\n\n";
    }
    out += b.md;
  });
  return out;
};
